<template>
  <div class="c-form-task-v2">
    <div class="c-form-task-v2__header">
      <div>
        <span class="c-form-task-v2__keyword-count">{{ checkedCount }}</span
        >キーワード選択中
      </div>
      <div class="c-form-task-v2__checked">
        <div class="c-form-task-v2__checked-reset">
          <button @click="resetChecked">
            <Icon2 variant="undo" size="14" />選択を解除
          </button>
        </div>
      </div>
    </div>
    <div class="c-form-task-v2__content">
      <div class="c-form-task-v2__col">
        <div class="c-form-task-v2-fieldset">
          <div class="c-form-task-v2-fieldset__header">
            <p class="c-form-task-v2-fieldset__title">施策の種類</p>
          </div>
          <div class="c-form-task-v2-fieldset__content c-alertPos">
            <Select
              v-model="params.type"
              :options="types"
              placeholder="施策の種類を選択"
              placement="top"
              :selected="params.type"
              size="sm"
              required
            />
            <Alert v-if="error.type">
              <span v-html="error.type"></span>
            </Alert>
          </div>
        </div>
      </div>
      <div class="c-form-task-v2__col">
        <div class="c-form-task-v2-fieldset">
          <div class="c-form-task-v2-fieldset__header">
            <p class="c-form-task-v2-fieldset__title">
              担当者<span class="c-form-task-v2-fieldset__title-optional"
                >任意</span
              >
            </p>
          </div>
          <div class="c-form-task-v2-fieldset__content c-alertPos">
            <Select
              v-model="params.operator"
              :options="projectUsers"
              placeholder="担当者なし"
              placement="top"
              :selected="params.operator"
              size="sm"
              :class="{
                alert: error.operator
              }"
            />
            <Alert v-if="error.operator">
              {{ error.operator }}
            </Alert>
          </div>
        </div>
      </div>
      <div class="c-form-task-v2__col c-form-task-v2__detail">
        <div class="c-form-task-v2-fieldset">
          <div class="c-form-task-v2-fieldset__header">
            <p class="c-form-task-v2-fieldset__title">詳細</p>
          </div>
          <div class="c-form-task-v2-fieldset__content c-alertPos">
            <Textarea
              v-model="params.text"
              placeholder="施策の詳細を記入してください"
              autosize
              size="sm"
              :rows="1"
              :class="{
                alert: isEdited.text && error.text
              }"
            />
            <Alert v-if="isEdited.text && error.text">
              {{ error.text }}
            </Alert>
          </div>
        </div>
        <div class="c-alertPos l-mt-3">
          <ButtonRectangle
            priority="high"
            size="large"
            :isLoading="isProcessing"
            :disabled="isDisabled"
            @click="submit"
            >タスクリストへ追加</ButtonRectangle
          >
          <Alert v-if="errorResponse">
            {{ errorResponse }}
          </Alert>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ERROR_MESSAGE, TASK_LENGTHS_LIMIT } from '@/constants';
import Alert from '@/components/Alert';
import ButtonRectangle from '@/components/ButtonRectangle';
import Icon2 from '@/components/Icon2';
import Select from '@/components/Select';
import Textarea from '@/components/Textarea';
import apiProjects from '@/api/projects';
import utils from '@/utils';
import Validation from '@/validation';

export default {
  props: {
    defaultType: {
      type: String,
      required: false
    },
    gaType: {
      type: String,
      required: true
    },
    items: {
      type: Array,
      required: true
    },
    project: {
      type: Object,
      required: true
    }
  },
  components: {
    Alert,
    ButtonRectangle,
    Icon2,
    Select,
    Textarea
  },
  data() {
    return {
      error: {
        operator: '',
        text: '',
        type: ''
      },
      errorResponse: '',
      params: {
        operator: '',
        text: '',
        type: this.defaultType ? this.defaultType : ''
      },
      usages: {
        rest: 0
      },
      isEdited: {
        operator: false,
        text: false,
        type: false
      },
      isError: false,
      isOver: false,
      isProcessing: false
    };
  },
  computed: {
    projectUsers() {
      return this.$store.getters['project/usersSelect'];
    },
    types() {
      return this.$store.getters['task/solutionSelect'];
    },
    isDisabled() {
      return !this.checkedCount || this.isError || this.isProcessing;
    },
    checkedCount() {
      return this.items.length;
    }
  },
  watch: {
    checkedCount: {
      handler() {
        this.resetError();
      }
    },
    error: {
      handler() {
        this.checkError();
      },
      deep: true
    },
    params: {
      handler() {
        this.resetError();
      },
      deep: true
    },
    'params.operator': {
      handler(value) {
        const operator = value;
        this.error.operator = '';
        this.isEdited.operator = operator ? true : false;
      },
      immediate: true
    },
    'params.text': {
      handler(value) {
        const text = value.trim();
        this.error.text = new Validation()
          .input()
          .maxLength(TASK_LENGTHS_LIMIT)
          .test(text);
        this.isEdited.text = text ? true : false;
      },
      immediate: true
    },
    'params.type': {
      handler(value) {
        const type = value;
        this.error.type = '';
        this.isEdited.type = type ? true : false;
      },
      immediate: true
    }
  },
  created() {
    this.$store.dispatch('project/getUsers');
    this.$store.dispatch('task/getTaskSolutions');
  },
  methods: {
    checkError() {
      this.isError = utils.checkError(this.error);
    },
    async submit() {
      if (this.isDisabled) {
        return;
      }
      this.isProcessing = true;
      this.errorResponse = '';

      await this.getUsages();
      if (this.errorResponse) {
        this.isProcessing = false;
        return;
      }
      if (this.params.operator) {
        await this.getUsers();
        if (this.error.operator) {
          this.isProcessing = false;
          return;
        }
      }
      await Promise.all(
        this.items.map(item => {
          return this.postTask(item);
        })
      )
        .then(() => {
          this.params.operator = '';
          this.params.text = '';
        })
        .catch(error => {
          if (error.response && error.response.data) {
            this.errorResponse = error.response.data.errors[0].userMessage;
          } else {
            this.errorResponse = ERROR_MESSAGE.other;
          }
        });

      this.$store.dispatch('project/getTaskCount');
      this.isProcessing = false;
    },
    getUsages() {
      const params = {
        items: 'tasks'
      };
      return apiProjects.usages
        .get(params, this.auth.idToken, this.project.id)
        .then(response => {
          const usages = response.data.items[0];
          const usagesRest = usages.max - usages.current;
          const error = utils.getErrorUsages({
            count: this.checkedCount,
            usages: usages,
            target: 'タスク'
          });
          if (error.message) {
            this.errorResponse = error.message;
          }
          if (usagesRest > 0 && usagesRest < this.checkedCount) {
            this.usages.rest = usagesRest;
            this.isOver = true;
          }
        })
        .catch(error => {
          if (error.response && error.response.data) {
            this.errorResponse = error.response.data.errors[0].userMessage;
          } else {
            this.errorResponse = ERROR_MESSAGE.other;
          }
        });
    },
    getUsers() {
      return this.$store.dispatch('project/getUsers').finally(() => {
        const existUser = this.projectUsers.some(
          user => user.value === this.params.operator
        );
        if (!existUser) {
          this.error.operator =
            '選択したユーザーが存在しません。担当者を再度設定してください';
        }
      });
    },
    postTask(item) {
      const params = {
        column: 'todo',
        keyword: null,
        operatorUserId: this.params.operator || null,
        taskDetail: this.params.text,
        type: this.params.type,
        url: null
      };

      params.keyword = item.keyword;
      params.url = item.url;

      this.setTaskDetail(params, item);

      return apiProjects.tasks.siteAnalysis.post(
        params,
        this.auth.idToken,
        this.project.id
      );
    },
    setTaskDetail(params, item) {
      let detail = params.taskDetail;
      const format = (label, value) => {
        return `\n【${label}】 ${value}`;
      };
      if (this.$route.name === 'siteImportantKeywordActions') {
        detail += '\n\n※タスク登録時の情報を反映しています';
        if (item.currentRank) {
          detail += format(
            '順位',
            `${utils.roundFloatingPointNumber(item.currentRank, 1)}位`
          );
        } else {
          detail += format('順位', '-');
        }
        if (item.clicks != null) {
          detail += format(
            '流入数',
            `${this.$options.filters.separateNumber(item.clicks)}`
          );
        }
        if (item.searchVolumes != null) {
          detail += format(
            '月間検索数',
            `${this.$options.filters.separateNumber(item.searchVolumes)}`
          );
        }
        if (item.impressions != null) {
          detail += format(
            '表示回数',
            `${this.$options.filters.separateNumber(item.impressions)}`
          );
        }
        if (item.ctr != null) {
          detail += format(
            'クリック率',
            `${this.$options.filters.roundFloatingPointNumber(
              item.ctr * 100,
              2
            )}%`
          );
        }
        if (this.gaType === 'ga4' && item.conversions != null) {
          detail += format(
            '貢献CV',
            `${this.$options.filters.separateNumber(
              this.$options.filters.roundFloatingPointNumber(
                item.conversions,
                2
              )
            )}`
          );
        }
      }
      params.taskDetail = detail;
    },
    resetChecked() {
      this.$emit('reset');
    },
    resetError() {
      if (!this.isOver || this.usages.rest >= this.checkedCount) {
        this.errorResponse = '';
        this.isOver = false;
      }
    }
  }
};
</script>
